import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { IconChevronDown, IconUsers } from '@tabler/icons-react';
import { Flex, Popover, ThemeIcon, Text, UnstyledButton } from '@mantine/core';
import { useMutation } from '@apollo/client';

import { ConfirmDeleteModal } from '../common';
import { HierarchyUser } from '../../codegen/graphql';
import { DELETE_USER } from '../../apollo/mutations/user';
import { showErrorNotification } from '../../notifications';
import StoreManagerDetailsItem from './StoreManagerDetailsItem';
import { StoreWithManagers } from '../../ts/interfaces/stores/store';
import { useUserPortal } from '../../context/UserPortalContext';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { GET_USERS } from '../../apollo/queries/user';
import { GET_STORES } from '../../apollo/queries/store';

type Props = Readonly<{
    store: StoreWithManagers;
    userCanManageStore: boolean;
}>;

function StoreManagerDetails({ store, userCanManageStore }: Props) {
    const { openTeamsModal, teamsModal, userSidesheet } = useUserPortal();

    const managers = useMemo(() => {
        if (!store.managers) {
            return [];
        }

        return store.managers;
    }, [store.managers]);

    const [managersPopoverOpened, setManagersPopoverOpened] = useState(false);
    const [confirmDeleteModal, setConfirmDeleteModal] = useState<{
        opened: boolean;
        user: HierarchyUser | null;
    }>({
        opened: false,
        user: null,
    });

    const [deleteUser, { error: deleteUserError, loading: deletingUser }] =
        useMutation(DELETE_USER, {
            refetchQueries: [GET_USERS, GET_OPS_HIERARCHY, GET_STORES],
            awaitRefetchQueries: true,
            variables: {
                id: confirmDeleteModal.user?.id,
            },
        });

    useEffect(() => {
        if (deleteUserError) {
            showErrorNotification('Error deleting user');
        }
    }, [deleteUserError]);

    const closeManagersPopover = useCallback(() => {
        setManagersPopoverOpened(false);
    }, []);

    const toggleManagersPopover = () => {
        setManagersPopoverOpened(!managersPopoverOpened);
    };

    useLayoutEffect(() => {
        if (teamsModal.opened || userSidesheet.opened) {
            setManagersPopoverOpened(false);
        }
    }, [teamsModal.opened, userSidesheet.opened]);

    const openMoveStoreManagerModal = useCallback(
        (manager: HierarchyUser) => () => {
            if (!manager) {
                return;
            }

            openTeamsModal({
                movingStoreManager: true,
                storeManager: manager,
                store,
            });
        },
        [openTeamsModal, store],
    );

    const openAssignManagerModal = useCallback(() => {
        openTeamsModal({
            addingStoreManager: true,
            store,
        });
    }, [store, openTeamsModal]);

    const closeConfirmDeleteModal = useCallback(() => {
        setConfirmDeleteModal({ opened: false, user: null });
    }, []);

    return (
        <div className="flex flex-1 w-full justify-between items-center pr-4">
            {managers.length <= 1 ? (
                <StoreManagerDetailsItem
                    manager={managers[0]}
                    moveManager={openMoveStoreManagerModal(managers[0])}
                    assignManager={openAssignManagerModal}
                    canAssignManager={managers.length < 2 && userCanManageStore}
                />
            ) : (
                <Popover
                    opened={managersPopoverOpened}
                    onDismiss={closeManagersPopover}
                    width={280}
                    shadow="md"
                    position="bottom-start"
                    withArrow
                    closeOnClickOutside={false}
                    trapFocus={false}
                >
                    <Popover.Target>
                        <UnstyledButton
                            type="button"
                            className="border border-solid border-gray-300 rounded-[4px] px-2 py-1 cursor-pointer"
                            onClick={toggleManagersPopover}
                        >
                            <Flex align="center" gap="1rem">
                                <ThemeIcon size="md">
                                    <IconUsers size="16px" />
                                </ThemeIcon>
                                <Flex align="center" gap="2px">
                                    <Text size="sm">Multiple Managers</Text>
                                    <IconChevronDown size="16px" />
                                </Flex>
                            </Flex>
                        </UnstyledButton>
                    </Popover.Target>
                    <Popover.Dropdown>
                        {managers.map((manager, index) => (
                            <StoreManagerDetailsItem
                                key={manager.id}
                                manager={manager}
                                divider={index !== managers.length - 1}
                                moveManager={openMoveStoreManagerModal(manager)}
                                assignManager={openAssignManagerModal}
                                canAssignManager={
                                    managers.length < 2 && userCanManageStore
                                }
                                nestedInPopover
                                onCloseManagersPopover={closeManagersPopover}
                            />
                        ))}
                    </Popover.Dropdown>
                </Popover>
            )}
            <ConfirmDeleteModal
                opened={confirmDeleteModal.opened}
                close={closeConfirmDeleteModal}
                onConfirm={deleteUser}
                subject="Store Manager"
                message={`Are you sure you want to delete ${confirmDeleteModal.user?.firstName} ${confirmDeleteModal.user?.lastName}?`}
                loading={deletingUser}
            />
        </div>
    );
}

export default StoreManagerDetails;
